import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Incident } from '../../Interfaces'
import { incidentService } from '../../services/IncidentService'
import { CircularProgress } from '@mui/material'
import Switch from '@mui/material/Switch'
import TextareaAutosize from '@mui/material/TextareaAutosize'
import DataContext from '../../context/dataProvider'
import { useContext } from 'react'

export default function IncidentDetail() {
  const INITIAL_VISIBLE_IMAGES = 4
  const IMAGES_PER_BATCH = 4
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser } = useContext(DataContext)
  const [incident, setIncident] = useState<Incident | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [newNote, setNewNote] = useState('')
  const [imageLoadingStates, setImageLoadingStates] = useState<boolean[]>([])
  const [isResolved, setIsResolved] = useState<boolean>(false)
  const [visibleImageCount, setVisibleImageCount] = useState<number>(INITIAL_VISIBLE_IMAGES)

  // Determine back navigation based on referrer
  const getBackNavigation = () => {
    // Check if we have state information about where we came from
    if (location.state?.from) {
      return location.state.from
    }

    // If coming from a location page, go back to that location's incidents tab
    if (incident?.walk?.locationId) {
      return `/app/locations/${incident.walk.locationId}#incidents`
    }

    // Default fallback to incidents list
    return '/app/incidents'
  }

  const handleBackNavigation = () => {
    navigate(getBackNavigation())
  }

  const getBackButtonText = () => {
    if (location.state?.from) {
      return 'Back to Location'
    }

    if (incident?.walk?.locationId) {
      return 'Back to Location'
    }

    return 'Back to Incidents'
  }

  const fetchIncident = useCallback(async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const data = await incidentService.getOne(Number(id))
      setIncident(data)
      setIsResolved(!!data.resolvedAt)
      setImageLoadingStates(new Array(data.images?.length || 0).fill(true))
      setVisibleImageCount(INITIAL_VISIBLE_IMAGES)
    } catch (error) {
      console.error('Error fetching incident:', error)
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchIncident()
  }, [fetchIncident])

  const handleAddNote = async () => {
    if (!incident || !newNote.trim() || !currentUser?.id) return

    try {
      await incidentService.addNote({
        incidentId: incident.id,
        note: newNote,
        userId: currentUser.id
      })
      await fetchIncident()
      setNewNote('')
    } catch (error) {
      console.error('Error adding note:', error)
    }
  }

  const handleToggleResolved = async () => {
    if (!incident) return

    try {
      await incidentService.resolve({
        incidentId: incident.id,
        resolved: !isResolved,
        resolvedAt: !isResolved ? new Date().toISOString() : null
      })
      setIsResolved(!isResolved)
      await fetchIncident()
    } catch (error) {
      console.error('Error updating resolved status:', error)
    }
  }

  const handleImageLoad = (index: number) => {
    setImageLoadingStates((prevStates) => {
      const newStates = [...prevStates]
      newStates[index] = false
      return newStates
    })
  }

  const handleLoadMoreImages = () => {
    setVisibleImageCount((prev) => prev + IMAGES_PER_BATCH)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CircularProgress />
      </div>
    )
  }

  if (!incident) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Incident Not Found</h2>
          <button
            onClick={handleBackNavigation}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {getBackButtonText()}
          </button>
        </div>
      </div>
    )
  }

  const sortedNotes = incident.notes ? [...incident.notes].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime()
    const dateB = new Date(b.createdAt).getTime()
    return dateB - dateA
  }) : []

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Incident {incident.id}</h1>
        <button
          onClick={handleBackNavigation}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          {getBackButtonText()}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status and Priority */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-wrap gap-4 items-center mb-4">
              {incident.typeId && (
                <div className="flex items-center">
                  <span className="text-gray-600 mr-2">Priority:</span>
                  {(() => {
                    switch (incident.typeId) {
                      case 1:
                        return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">Critical</span>
                      case 2:
                        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">Important</span>
                      case 3:
                        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Regular</span>
                      default:
                        return null
                    }
                  })()}
                </div>
              )}
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">Status:</span>
                <Switch
                  checked={isResolved}
                  onChange={handleToggleResolved}
                  color="primary"
                />
                <span className="ml-2">{isResolved ? 'Resolved' : 'Unresolved'}</span>
              </div>
            </div>

            {incident.incidentTexts?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-gray-600 mr-2">Types:</span>
                {incident.incidentTexts.map((text) => (
                  <span key={text.id} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {text.incidentNoteText.text}
                  </span>
                ))}
              </div>
            )}

            <div className="text-gray-600">
              <p>Location: {incident.walk?.location?.name || 'Unknown Location'}</p>
              <p>Created: {new Date(incident.createdAt).toLocaleString()}</p>
              {incident.resolvedAt && (
                <p>Resolved: {new Date(incident.resolvedAt).toLocaleString()}</p>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Notes</h2>
            <div className="space-y-4">
              {sortedNotes.map((note) => (
                <div key={note.id} className="border-b pb-4 last:border-b-0">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">
                      {note.user?.firstName && note.user?.lastName
                        ? `${note.user.firstName} ${note.user.lastName}`
                        : 'Unknown User'}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {new Date(note.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{note.note}</p>
                </div>
              ))}
            </div>

            {/* Add Note Form */}
            <div className="mt-6">
              <TextareaAutosize
                minRows={3}
                placeholder="Add a new note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Note
              </button>
            </div>
          </div>
        </div>

        {/* Images Section */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Images</h2>
            <div className="space-y-4">
              {incident.images?.slice(0, visibleImageCount).map((image, index) => {
                const imageSrc =
                  image.displayPresignedUrl || image.presignedUrl || image.imageUrl
                return (
                <div key={image.id ?? index} className="relative min-h-64 rounded-lg bg-gray-200 overflow-hidden">
                  {!imageSrc ? (
                    <div className="w-full h-64 flex items-center justify-center text-sm text-gray-500">
                      Image unavailable
                    </div>
                  ) : (
                    <>
                      {imageLoadingStates[index] && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-200">
                          <CircularProgress />
                        </div>
                      )}
                      {/* Keep the img in the layout (not display:none) so the browser actually fetches it.
                          loading="lazy" + hidden prevented any network request and left the spinner forever. */}
                      <img
                        src={imageSrc}
                        alt={`Incident ${incident.id} - ${index + 1}`}
                        className="w-full h-auto object-contain rounded-lg"
                        decoding="async"
                        onLoad={() => handleImageLoad(index)}
                        onError={() => handleImageLoad(index)}
                      />
                    </>
                  )}
                </div>
                )
              })}
              {incident.images && incident.images.length > visibleImageCount && (
                <button
                  type="button"
                  onClick={handleLoadMoreImages}
                  className="w-full px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-sm font-medium text-gray-700"
                >
                  Load More Images ({incident.images.length - visibleImageCount} remaining)
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 